import React, { FC } from "react";
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useState } from 'react';
import ContactOptions, { ContactChoices, FormOption } from "./ContactOptions";
import axios from "axios";
import { lddGlobalSessionInfo } from "../navigationTypes";


export interface ReportFormProps {
    showModal: boolean,
    handleShowModal: () => void,
    handleSubmitResponse: (success: boolean) => void,
    SESSION_INFO: lddGlobalSessionInfo
}

const issues = [
    "Incorrect information generated",
    "Information was missing",
    "Feature not functioning as expected",
    "Other"
];

const impacts = [
    "Minimum impact. Should be addressed for future use",
    "Impact file usage",
    "High impact. Cannot close transaction"
];

const ReportForm: FC<ReportFormProps> = ({showModal, handleShowModal, handleSubmitResponse, SESSION_INFO}) => {
    const [show, setShow] = useState(showModal);
    const [feedback, setFeedback] = useState("");
    const [selectedIssue, setSelectedIssue] = useState<string>();
    const [selectedImpact, setSelectedImpact] = useState<string>();
    const [contactChoice, setContactChoice] = useState(ContactChoices.Email);
    const [contactInfo, setContactInfo] = useState(SESSION_INFO.user.email);
    const [errors, setErrors] = useState({});
    const [validationSuccess, setValidationSuccess] = useState(false);

    const URLsid = SESSION_INFO?.sid;

    const handleClose = () => {
        setShow(false);
        handleShowModal();
    };

    const handleIssueChange = (e) => {
        setErrors({...errors, issue: ''});
        setSelectedIssue(e.target.value);
    };

    const handleImpactChange = (e) => {
        setErrors({...errors, impact: ''});
        setSelectedImpact(e.target.value);
    };

    const handleContactChoiceChange = (option: ContactChoices) => {
        setContactChoice(option);
        setErrors({...errors, email: '', phone: ''});
    };

    const handleContactInfoChange = (info: string) => {
        setContactInfo(info);
        setErrors({...errors, email: '', phone: ''});
    };

    const validate = () => {
        const newErrors = {};
        if (contactChoice === ContactChoices.Email && !contactInfo.includes('@')) {
          newErrors['email'] = 'Email is invalid';
        }
        if (contactChoice === ContactChoices.Email && contactInfo === "") {
            newErrors['email'] = 'Email is missing';
        }
        if (contactChoice === ContactChoices.Phone && contactInfo === "") {
            newErrors['phone'] = 'Phone is missing';
        }
        if (!issues.includes(selectedIssue)) {
          newErrors['issue'] = 'This field must be selected';
        }
        if (!impacts.includes(selectedImpact)) {
            newErrors['impact'] = 'This field must be selected';
        }
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length === 0) {
            setValidationSuccess(true);
            const errorText = `<div><div><b>Preferred Contact Method:</b>&nbsp;${contactChoice}<br></div><ul><li>Email:&nbsp;${contactChoice === ContactChoices.Email ? contactInfo : ''}<br></li><li>Phone:&nbsp${contactChoice === ContactChoices.Phone ? contactInfo : ''};<br></li></ul><div><br></div><div><b>Bug Explanation:</b><br></div><div>${feedback}<br></div><div><br></div><div><hr><div style=\"overflow: scroll\"><table style=\"font-size: 13px\"> <tbody><tr><td valign=\"top\">Name</td><td valign=\"top\">:</td><td valign=\"top\">${SESSION_INFO.user.firstname}, ${SESSION_INFO.user.lastname}</td></tr><tr><td valign=\"top\">Email</td><td valign=\"top\">:</td><td valign=\"top\">${SESSION_INFO.user.email}</td></tr><tr><td valign=\"top\">Which of the following best describes the error you encountered?</td><td valign=\"top\">:</td><td valign=\"top\">${selectedIssue}</td></tr><tr><td valign=\"top\">What is the impact of the error on your file?</td><td valign=\"top\">:</td><td valign=\"top\">${selectedImpact}</td></tr><tr><td valign=\"top\">Describe the error in the text box below.</td><td valign=\"top\">:</td><td valign=\"top\">${feedback}</td></tr><tr><td valign=\"top\">Upload Screenshot or Other Helpful Documents</td><td valign=\"top\">:</td><td valign=\"top\"></td></tr><tr><td valign=\"top\">How do you prefer we follow up with you?</td><td valign=\"top\">:</td><td valign=\"top\">${contactChoice}</td></tr><tr><td valign=\"top\">${contactChoice}</td><td valign=\"top\">:</td><td valign=\"top\">${contactInfo}</td></tr></tbody></table></div><br></div></div><div><br></div>`
            try {
                handleClose();
                const response = await axios.post(
                    `/${process.env.REACT_APP_UVAPI_ENDPOINT}/api/createZohoTicket?SessionID=${URLsid}`,
                    {
                        channel: "Web",
                        classification: "Bug/Enhancement Request",
                        subject: 'Reported Error from ' + SESSION_INFO.user.fullname + ' at ' + SESSION_INFO.firm.name,
                        status: "Open",
                        contact: {
                            email: SESSION_INFO.user.email,
                        },
                        departmentId: "180615000000006907",
                        description: errorText,
                        email: SESSION_INFO.user.email,
                    },
                );
                if  (response.status === 200) {
                    handleSubmitResponse(true);
                }
                console.log('Ticket created successfully:', response.data);
            } catch (error) {
                handleSubmitResponse(false);
                console.error('Error creating ticket:', error.response.data);
            }
        } else {
            setErrors(validationErrors);
            setValidationSuccess(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} backdrop="static">
            <Modal.Header>
                <Modal.Title>Report an Error</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit} id="errorForm">
                    <Form.Group controlId="userInfo">
                        <Row className="mb-3">
                            <Col>
                                <Form.Label className="fw-bold">First Name</Form.Label>
                                <Form.Control disabled value={SESSION_INFO.user.firstname}></Form.Control>
                            </Col>
                            <Col xs={6}>
                                <Form.Label className="fw-bold">Last Name</Form.Label>
                                <Form.Control disabled value={SESSION_INFO.user.lastname}></Form.Control>
                            </Col>
                        </Row>
                    </Form.Group>
                    <Form.Group controlId="issues">
                        <Form.Label className="fw-bold">Which of the following best describes the error you encountered?<span className="required">*</span></Form.Label>
                        <Form.Select
                            value={selectedIssue}
                            onChange={handleIssueChange}
                        >
                            <option disabled selected>-- Select One --</option>
                            {issues.map(issue => <option value={issue}>{issue}</option>)}
                        </Form.Select>
                        {!validationSuccess && <b className="input-error">{errors['issue']}</b>}
                    </Form.Group>

                    <Form.Group controlId="impact" className="mt-3">
                        <Form.Label className="fw-bold">What is the impact of the error on your file?<span className="required">*</span></Form.Label>
                        <Form.Select
                            value={selectedImpact}
                            onChange={handleImpactChange}
                        >
                            <option disabled selected>-- Select One --</option>
                            {impacts.map(impact => <option value={impact}>{impact}</option>)}
                        </Form.Select>
                        {!validationSuccess && <b className="input-error">{errors['impact']}</b>}
                    </Form.Group>
                    <Form.Group controlId="formErrorText" className="mt-3">
                        <Form.Label className="fw-bold">Describe the error in the text box below.</Form.Label>
                        <Form.Control 
                            as="textarea" 
                            placeholder="Enter text..." 
                            rows={5} onChange={(e) => setFeedback(e.target.value)} 
                            >
                        </Form.Control>
                    </Form.Group>
                    {/* Temporarily hide the file uploader */}
                    {/* <Form.Group controlId="formErrorAttachments">
                        <Form.Label>{"Upload a file (optional)"}</Form.Label>
                        <FileUploader
                            type={FileUploadType.ErrorReport}
                            fileType="image"
                            multiple={false}
                            fileAccept="image/png, image/gif, image/jpeg"
                            updateFile={setFiles}
                        />
                    </Form.Group> */}
                    <ContactOptions
                        initialEmail={SESSION_INFO.user.email}
                        formOption={FormOption.Issue}
                        updateContactChoice={handleContactChoiceChange}
                        updateContactInfo={handleContactInfoChange}
                        errorMsgs={errors}
                    />
                </Form>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <Button variant="link" onClick={handleClose}>
                    Cancel
                </Button>
                <Button variant="primary" type="submit" form="errorForm">
                    Submit
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default ReportForm;